import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { ASYNC_TASK_STATUS, ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { boardContextApi, asyncTasksListing, uploadFile } from "@/network/network.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";
import type { BoardContextGroup } from "@/bryntum-lib/models/BoardContextGroup";
import type { BoardContextStatement } from "@/bryntum-lib/models/BoardContextStatement";
import type { DraftQuestion } from "@/bryntum-lib/models/BoardContext";
import {
  boardTasksForBoard,
  hasDraftQuestionsReady,
  isContextViewReady,
  resolveTrackedTask,
  resolveWizardStep,
  taskMatchesBoard,
  type WizardStep,
} from "./boardContextWizard";

const RESUME_FLOW_STEPS: WizardStep[] = ["setup", "page-picker", "analysing", "questions"];

export type { WizardStep };

export interface CrawledPage {
  url: string;
  title: string;
  description: string;
}

interface BoardContextContextValue {
  step: WizardStep;
  boardContextRecord: BoardContext | null;
  groups: BoardContextGroup[];
  statements: BoardContextStatement[];

  // Setup step
  isLoadingMapUrl: boolean;
  crawledPages: CrawledPage[];
  hasContextView: boolean;
  hasDraftQuestions: boolean;
  goToContextView: () => void;
  goToQuestions: () => void;
  handleSetupSubmit: (data: {
    websiteUrl: string;
    businessDescription: string;
    file: File | null;
    fileName: string;
  }) => Promise<void>;

  // Page picker step
  selectedUrls: string[];
  setSelectedUrls: (urls: string[]) => void;
  handleAnalysePages: () => Promise<void>;
  isSubmittingPagePick: boolean;
  canContinueToQuestions: boolean;
  handleContinueToQuestions: () => void;

  // Analysing / questions step
  draftQuestions: DraftQuestion[];
  answers: Record<number, string[]>;
  setAnswer: (index: number, answer: string[]) => void;
  handleBuildContext: (withoutAnswers?: boolean) => Promise<void>;
  isSubmittingAnswers: boolean;
  handleBackFromQuestions: () => void;

  // Re-generate
  handleRegenerate: () => void;

  // Strategic suggestions
  isGeneratingSuggestions: boolean;
  handleGenerateSuggestions: () => Promise<void>;

  // Loaders
  isInitialising: boolean;

  hasOpenAIKey: boolean;
}

const BoardContextContext = createContext<BoardContextContextValue | undefined>(undefined);

const POLL_INTERVAL_MS = 10_000;

/** Default number of crawled pages pre-selected on first visit to the page picker (no max cap). */
const DEFAULT_PAGE_PICKER_SELECTION = 8;

const defaultSelectedUrls = (pages: CrawledPage[]) =>
  pages.slice(0, DEFAULT_PAGE_PICKER_SELECTION).map((p) => p.url);

const selectedUrlsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((url) => setB.has(url));
};

const LOADER_STEPS: WizardStep[] = ["analysing", "building"];
const STEPS_BEFORE_LOADERS: WizardStep[] = ["setup", "page-picker", "questions"];

export const BoardContextProvider = ({ children }: { children: ReactNode }) => {
  const { project, selectedBoard, userPreferences } = useSwivuBryntumContext();
  const slug = useMemo(() => (selectedBoard?.record as any)?.slug as string | undefined, [selectedBoard]);
  const boardId = useMemo(() => selectedBoard?.id, [selectedBoard]);
  const api = useMemo(() => (slug ? boardContextApi(slug) : null), [slug]);

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey,
  });

  const hasOpenAIKey = userPreferences
    ? userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true"
    : false;

  // ── Subscribe to loadsync stores ──────────────────────────────────────────
  const { version: boardContextVersion } = useSwivuStore({ storeId: STORE_KEYS.boardContextStore });
  const { version: boardContextGroupVersion } = useSwivuStore({
    storeId: STORE_KEYS.boardContextGroupStore,
  });
  const { version: boardContextStatementVersion } = useSwivuStore({
    storeId: STORE_KEYS.boardContextStatementStore,
  });

  // ── Derived data from stores ──────────────────────────────────────────────
  const boardContextRecord = useMemo<BoardContext | null>(() => {
    if (!project) return null;
    const store = project.getCrudStore(STORE_KEYS.boardContextStore);
    return (store?.first as unknown as BoardContext) ?? null;
  }, [project, boardContextVersion]);

  const groups = useMemo<BoardContextGroup[]>(() => {
    if (!project || !boardContextRecord) return [];
    const store = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
    if (!store) return [];
    return (store.records as unknown as BoardContextGroup[])
      .filter((g) => g.boardContext === boardContextRecord.id)
      .sort((a, b) => a.order - b.order);
  }, [project, boardContextRecord, boardContextGroupVersion]);

  const statements = useMemo<BoardContextStatement[]>(() => {
    if (!project) return [];
    const store = project.getCrudStore(STORE_KEYS.boardContextStatementStore);
    if (!store) return [];
    return (store.records as unknown as BoardContextStatement[]).sort((a, b) => a.order - b.order);
  }, [project, boardContextStatementVersion]);

  // ── Local state ───────────────────────────────────────────────────────────
  const [step, setStep] = useState<WizardStep>("setup");
  const [isInitialising, setIsInitialising] = useState(true);
  const [isLoadingMapUrl, setIsLoadingMapUrl] = useState(false);
  const [crawledPages, setCrawledPages] = useState<CrawledPage[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [isSubmittingPagePick, setIsSubmittingPagePick] = useState(false);
  const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);

  // Track in-flight async task IDs
  const questionsTaskIdRef = useRef<string | null>(null);
  const contextTaskIdRef = useRef<string | null>(null);
  const suggestionTaskIdRef = useRef<string | null>(null);
  const processedTaskIdsRef = useRef<Set<string>>(new Set());
  /** True after "Update sources" until question generation restarts — avoids poll snapping back to context-view while status is still completed. */
  const resumeSourcesFlowRef = useRef(false);
  /** Step the user explicitly navigated to; poll must not override until resolver agrees or async loaders take over. */
  const manualStepRef = useRef<WizardStep | null>(null);
  /** Optimistic loader step between submit and task list / record sync. */
  const inFlightStepRef = useRef<WizardStep | null>(null);

  const setWizardStep = useCallback((next: WizardStep, source: "manual" | "auto") => {
    if (source === "manual") {
      manualStepRef.current = next;
      inFlightStepRef.current = null;
    }
    setStep(next);
  }, []);

  const beginInFlightStep = useCallback((loaderStep: "analysing" | "building") => {
    inFlightStepRef.current = loaderStep;
    manualStepRef.current = null;
    if (loaderStep === "building") {
      resumeSourcesFlowRef.current = false;
    }
    setStep(loaderStep);
  }, []);

  const clearInFlightStep = useCallback(() => {
    inFlightStepRef.current = null;
  }, []);

  const restorePagePickerFromRecord = useCallback(
    async (record: BoardContext | null) => {
      if (!record) return;
      setSelectedUrls(record.selectedUrls ?? []);
      if (record.websiteUrl && api) {
        setIsLoadingMapUrl(true);
        try {
          const res = await api.mapUrl(record.websiteUrl);
          setCrawledPages(res.data.links ?? []);
        } catch {
          setCrawledPages([]);
        } finally {
          setIsLoadingMapUrl(false);
        }
      }
    },
    [api]
  );

  const applyResolvedStep = useCallback(
    async (record: BoardContext | null, allTasks: any[]) => {
      const tracked = {
        questionsTaskId: questionsTaskIdRef.current,
        contextTaskId: contextTaskIdRef.current,
      };
      let next = resolveWizardStep(record, allTasks, boardId, tracked);

      if (tracked.contextTaskId) {
        const cTasks = boardTasksForBoard(
          allTasks,
          ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
          boardId
        );
        const cTask = resolveTrackedTask(cTasks, tracked.contextTaskId);
        if (!cTask || cTask.status === ASYNC_TASK_STATUS.PENDING) {
          next = "building";
        } else if (cTask.status === ASYNC_TASK_STATUS.COMPLETED) {
          next = "context-view";
          contextTaskIdRef.current = null;
          inFlightStepRef.current = null;
        }
      } else if (tracked.questionsTaskId) {
        const qTasks = boardTasksForBoard(
          allTasks,
          ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
          boardId
        );
        const qTask = resolveTrackedTask(qTasks, tracked.questionsTaskId);
        if (!qTask || qTask.status === ASYNC_TASK_STATUS.PENDING) {
          next = "analysing";
        } else if (
          qTask.status === ASYNC_TASK_STATUS.COMPLETED &&
          hasDraftQuestionsReady(record)
        ) {
          next = "questions";
          questionsTaskIdRef.current = null;
          inFlightStepRef.current = null;
        }
      }

      const inFlight = inFlightStepRef.current;
      if (inFlight === "building" && next !== "context-view") {
        const cTasks = boardTasksForBoard(
          allTasks,
          ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
          boardId
        );
        const stillRunning =
          cTasks.some((t) => t.status === ASYNC_TASK_STATUS.PENDING) ||
          Boolean(
            contextTaskIdRef.current &&
              resolveTrackedTask(cTasks, contextTaskIdRef.current)?.status ===
                ASYNC_TASK_STATUS.PENDING
          );
        if (stillRunning) next = "building";
      } else if (inFlight === "analysing" && next !== "questions") {
        const qTasks = boardTasksForBoard(
          allTasks,
          ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
          boardId
        );
        const stillRunning =
          qTasks.some((t) => t.status === ASYNC_TASK_STATUS.PENDING) ||
          Boolean(
            questionsTaskIdRef.current &&
              resolveTrackedTask(qTasks, questionsTaskIdRef.current)?.status ===
                ASYNC_TASK_STATUS.PENDING
          );
        if (stillRunning) next = "analysing";
      }

      if (next === "page-picker") {
        await restorePagePickerFromRecord(record);
      }
      setStep((prev) => {
        if (
          resumeSourcesFlowRef.current &&
          next === "context-view" &&
          RESUME_FLOW_STEPS.includes(prev)
        ) {
          return prev;
        }

        if (next === "context-view") {
          inFlightStepRef.current = null;
          manualStepRef.current = null;
          return prev === next ? prev : next;
        }

        if (next === "analysing" || next === "building") {
          manualStepRef.current = null;
          return prev === next ? prev : next;
        }

        if (next === "questions" && inFlightStepRef.current === "analysing") {
          inFlightStepRef.current = null;
        }

        const shouldBlockDowngrade =
          (LOADER_STEPS.includes(prev) && STEPS_BEFORE_LOADERS.includes(next)) ||
          (inFlightStepRef.current != null &&
            STEPS_BEFORE_LOADERS.includes(next) &&
            next !== inFlightStepRef.current);

        if (shouldBlockDowngrade) {
          if (
            inFlightStepRef.current ||
            questionsTaskIdRef.current ||
            contextTaskIdRef.current
          ) {
            return prev;
          }
        }

        const manual = manualStepRef.current;
        if (manual != null && next !== manual) {
          return prev;
        }
        if (manual != null && next === manual) {
          manualStepRef.current = null;
        }

        return prev === next ? prev : next;
      });
    },
    [boardId, restorePagePickerFromRecord]
  );

  const markOlderTasksProcessed = (tasks: { id: number | string }[]) => {
    for (const t of tasks.slice(1)) {
      processedTaskIdsRef.current.add(String(t.id));
    }
  };

  // ── Async task polling (mirrors SMMGeneratorContext) ──────────────────────
  useEffect(() => {
    if (!boardId) return;

    let isMounted = true;
    let hasInitialised = false;

    const poll = async () => {
      try {
        const res = await asyncTasksListing();
        if (!isMounted) return;

        const allTasks: any[] = res.data ?? [];

        const bcStore = project?.getCrudStore(STORE_KEYS.boardContextStore);
        const boardContextExists = Boolean(bcStore?.first);
        const latestRecord = boardContextExists
          ? ((bcStore?.first as unknown as BoardContext) ?? null)
          : null;

        if (boardContextExists && latestRecord) {
          const qTasks = boardTasksForBoard(
            allTasks,
            ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
            boardId
          );
          markOlderTasksProcessed(qTasks);
          const qTask = resolveTrackedTask(qTasks, questionsTaskIdRef.current);

          if (qTask && !processedTaskIdsRef.current.has(String(qTask.id))) {
            if (qTask.status === ASYNC_TASK_STATUS.PENDING) {
              questionsTaskIdRef.current = String(qTask.id);
            } else if (qTask.status === ASYNC_TASK_STATUS.COMPLETED) {
              processedTaskIdsRef.current.add(String(qTask.id));
              const inFlightId = questionsTaskIdRef.current;
              questionsTaskIdRef.current = null;
              if (inFlightId === String(qTask.id)) {
                inFlightStepRef.current = null;
              }
            } else if (
              qTask.status === ASYNC_TASK_STATUS.FAILED ||
              qTask.status === ASYNC_TASK_STATUS.CANCELLED
            ) {
              processedTaskIdsRef.current.add(String(qTask.id));
              const inFlightId = questionsTaskIdRef.current;
              questionsTaskIdRef.current = null;
              if (inFlightId === String(qTask.id)) {
                inFlightStepRef.current = null;
              }
              if (inFlightId === String(qTask.id) && isMounted) {
                showToast({
                  msg: "Question generation failed. Please try again.",
                  type: "error",
                  duration: 4000,
                });
              }
            }
          }

          const cTasks = boardTasksForBoard(
            allTasks,
            ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
            boardId
          );
          markOlderTasksProcessed(cTasks);
          const cTask = resolveTrackedTask(cTasks, contextTaskIdRef.current);

          if (cTask && !processedTaskIdsRef.current.has(String(cTask.id))) {
            if (cTask.status === ASYNC_TASK_STATUS.PENDING) {
              contextTaskIdRef.current = String(cTask.id);
            } else if (cTask.status === ASYNC_TASK_STATUS.COMPLETED) {
              processedTaskIdsRef.current.add(String(cTask.id));
              const inFlightId = contextTaskIdRef.current;
              if (inFlightId === String(cTask.id)) {
                inFlightStepRef.current = null;
                contextTaskIdRef.current = null;
              }
            } else if (
              cTask.status === ASYNC_TASK_STATUS.FAILED ||
              cTask.status === ASYNC_TASK_STATUS.CANCELLED
            ) {
              processedTaskIdsRef.current.add(String(cTask.id));
              const inFlightId = contextTaskIdRef.current;
              contextTaskIdRef.current = null;
              if (inFlightId === String(cTask.id)) {
                inFlightStepRef.current = null;
              }
              if (inFlightId === String(cTask.id) && isMounted) {
                showToast({
                  msg: "Context generation failed. Please try again.",
                  type: "error",
                  duration: 4000,
                });
              }
            }
          }

          const sTasks = boardTasksForBoard(
            allTasks,
            ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION,
            boardId
          );
          markOlderTasksProcessed(sTasks);
          const sTask = resolveTrackedTask(sTasks, suggestionTaskIdRef.current);
          const hasPendingSuggestion = sTasks.some(
            (t) => t.status === ASYNC_TASK_STATUS.PENDING
          );

          if (hasPendingSuggestion && isMounted) {
            setIsGeneratingSuggestions(true);
          }

          if (sTask && !processedTaskIdsRef.current.has(String(sTask.id))) {
            if (sTask.status === ASYNC_TASK_STATUS.PENDING) {
              suggestionTaskIdRef.current = String(sTask.id);
              if (isMounted) setIsGeneratingSuggestions(true);
            } else if (sTask.status === ASYNC_TASK_STATUS.COMPLETED) {
              processedTaskIdsRef.current.add(String(sTask.id));
              const inFlightId = suggestionTaskIdRef.current;
              suggestionTaskIdRef.current = null;
              if (isMounted) {
                setIsGeneratingSuggestions(false);
                if (inFlightId === String(sTask.id)) {
                  showToast({
                    msg: "Strategic suggestions are ready to review.",
                    type: "success",
                    duration: 4000,
                  });
                }
              }
            } else if (
              sTask.status === ASYNC_TASK_STATUS.FAILED ||
              sTask.status === ASYNC_TASK_STATUS.CANCELLED
            ) {
              processedTaskIdsRef.current.add(String(sTask.id));
              const inFlightId = suggestionTaskIdRef.current;
              suggestionTaskIdRef.current = null;
              if (isMounted) {
                setIsGeneratingSuggestions(hasPendingSuggestion);
              }
              if (inFlightId === String(sTask.id)) {
                showToast({
                  msg: "Strategic suggestions generation failed. Please try again.",
                  type: "error",
                  duration: 4000,
                });
              }
            }
          } else if (!hasPendingSuggestion && !suggestionTaskIdRef.current && isMounted) {
            setIsGeneratingSuggestions(false);
          }

          if (isMounted) {
            await applyResolvedStep(latestRecord, allTasks);
          }
        } else if (isMounted) {
          manualStepRef.current = null;
          inFlightStepRef.current = null;
          setStep((prev) => (prev === "setup" ? prev : "setup"));
        }

        if (!hasInitialised && isMounted) {
          hasInitialised = true;
          setIsInitialising(false);
        }
      } catch {
        if (!hasInitialised && isMounted) {
          hasInitialised = true;
          setIsInitialising(false);
        }
      }
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [boardId, project, applyResolvedStep]);

  // ── Setup submit ─────────────────────────────────────────────────────────
  const handleSetupSubmit = useCallback(
    async ({
      websiteUrl,
      businessDescription,
      file,
      fileName,
    }: {
      websiteUrl: string;
      businessDescription: string;
      file: File | null;
      fileName: string;
    }) => {
      if (!project || !api) return;
      const store = project.getCrudStore(STORE_KEYS.boardContextStore);
      if (!store) return;

      // Upload PDF if provided
      let privateDocumentId: number | null = null;
      if (file) {
        try {
          const uploadRes = await uploadFile(file, fileName);
          privateDocumentId = uploadRes?.data?.id ?? null;
        } catch {
          showToast({ msg: "Failed to upload document. Please try again.", type: "error", duration: 4000 });
          return;
        }
      }

      // Create or update the boardcontexts record via loadsync (use Bryntum field names, not API keys)
      const existing = (store.first as unknown as BoardContext) ?? null;
      const patch = {
        websiteUrl: websiteUrl || null,
        businessDescription: businessDescription || null,
        ...(privateDocumentId !== null ? { privateDocument: privateDocumentId } : {}),
      };

      if (existing) {
        existing.set(patch);
      } else {
        store.add({ board: boardId, ...patch });
      }

      try {
        await project.sync();
      } catch {
        showToast({ msg: "Failed to save business context sources.", type: "error", duration: 4000 });
        return;
      }

      // If URL provided, crawl pages next
      if (websiteUrl) {
        setIsLoadingMapUrl(true);
        try {
          const res = await api.mapUrl(websiteUrl);
          const links: CrawledPage[] = res.data.links ?? [];
          setCrawledPages(links);
          setSelectedUrls(defaultSelectedUrls(links));
          setWizardStep("page-picker", "manual");
        } catch {
          showToast({ msg: "Could not crawl the URL. You can still proceed with description/document.", type: "error", duration: 4000 });
          // URL crawl failed — go straight to generate-questions (no pages to save)
          await triggerGenerateQuestions("setup");
        } finally {
          setIsLoadingMapUrl(false);
        }
      } else {
        // No URL — skip page picker, go straight to generate-questions
        await triggerGenerateQuestions("setup");
      }
    },
    [project, api, boardId, setWizardStep]
  );

  // ── Trigger generate-questions (internal helper) ──────────────────────────
  // Precondition: selected_urls must already be synced to the backend before calling this.
  const triggerGenerateQuestions = useCallback(
    async (fallbackStep: WizardStep) => {
      if (!api) return;
      beginInFlightStep("analysing");
      try {
        const res = await api.generateQuestions();
        resumeSourcesFlowRef.current = false;
        const newTaskId = String(res.data.id);
        questionsTaskIdRef.current = newTaskId;
        // Ignore older board question-generation tasks so stale Cancelled rows cannot fire later.
        try {
          const listRes = await asyncTasksListing();
          for (const t of listRes.data ?? []) {
            if (
              t.type === ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION &&
              taskMatchesBoard(t, boardId) &&
              String(t.id) !== newTaskId
            ) {
              processedTaskIdsRef.current.add(String(t.id));
            }
          }
        } catch {
          // Polling will still mark older tasks via markOlderTasksProcessed
        }
      } catch {
        clearInFlightStep();
        questionsTaskIdRef.current = null;
        setWizardStep(fallbackStep, "manual");
        showToast({ msg: "Failed to start question generation.", type: "error", duration: 4000 });
      }
    },
    [api, boardId, beginInFlightStep, clearInFlightStep, setWizardStep]
  );

  // ── Page picker submit ────────────────────────────────────────────────────
  const handleAnalysePages = useCallback(async () => {
    if (!project) return;
    setIsSubmittingPagePick(true);
    try {
      // 1. Persist selected_urls to boardcontexts store and sync to backend FIRST
      const store = project.getCrudStore(STORE_KEYS.boardContextStore);
      const existing = (store?.first as unknown as BoardContext) ?? null;
      if (existing) {
        existing.selectedUrls = selectedUrls;
        await project.sync();
      }

      // 2. Now trigger generate-questions (backend can read the saved selected_urls)
      await triggerGenerateQuestions("page-picker");
    } finally {
      setIsSubmittingPagePick(false);
    }
  }, [project, selectedUrls, triggerGenerateQuestions]);

  // ── Answer management ─────────────────────────────────────────────────────
  const setAnswer = useCallback((index: number, answer: string[]) => {
    setAnswers((prev) => ({ ...prev, [index]: answer }));
  }, []);

  // ── Build context ─────────────────────────────────────────────────────────
  const handleBuildContext = useCallback(
    async (withoutAnswers = false) => {
      if (!api || !boardContextRecord?.draftQuestions?.questions) return;

      if (withoutAnswers) {
        setAnswers({});
      }

      setIsSubmittingAnswers(true);

      const questions = boardContextRecord.draftQuestions.questions.map((q, i) => ({
        text: q.text,
        question_type: q.question_type,
        options: q.options,
        answer: withoutAnswers ? [] : (answers[i] ?? []),
      }));

      try {
        beginInFlightStep("building");
        const res = await api.generateContext(questions);
        contextTaskIdRef.current = String(res.data.id);
        // Polling loop drives the rest
      } catch {
        clearInFlightStep();
        contextTaskIdRef.current = null;
        setWizardStep("questions", "manual");
        showToast({ msg: "Failed to start context generation.", type: "error", duration: 4000 });
      } finally {
        setIsSubmittingAnswers(false);
      }
    },
    [api, boardContextRecord, answers, beginInFlightStep, clearInFlightStep, setWizardStep]
  );

  // ── Back from questions (page-picker or setup if pages were skipped) ─────
  const handleBackFromQuestions = useCallback(() => {
    setAnswers({});
    const hasSavedPageSelection = (boardContextRecord?.selectedUrls ?? []).length > 0;
    if (hasSavedPageSelection) {
      setSelectedUrls(boardContextRecord?.selectedUrls ?? []);
      setWizardStep("page-picker", "manual");
      void restorePagePickerFromRecord(boardContextRecord);
    } else {
      setWizardStep("setup", "manual");
    }
  }, [boardContextRecord, restorePagePickerFromRecord, setWizardStep]);

  const hasDraftQuestions = useMemo(
    () => hasDraftQuestionsReady(boardContextRecord),
    [boardContextRecord, boardContextVersion]
  );

  const selectedUrlsMatchSaved = useMemo(() => {
    const saved = boardContextRecord?.selectedUrls ?? [];
    return selectedUrlsEqual(selectedUrls, saved);
  }, [selectedUrls, boardContextRecord, boardContextVersion]);

  const canContinueToQuestions =
    boardContextRecord?.status === "in_progress" &&
    hasDraftQuestions &&
    selectedUrlsMatchSaved &&
    selectedUrls.length > 0;

  const handleContinueToQuestions = useCallback(() => {
    setWizardStep("questions", "manual");
  }, [setWizardStep]);

  const hasContextView = useMemo(
    () => isContextViewReady(boardContextRecord),
    [boardContextRecord, boardContextVersion]
  );

  const goToContextView = useCallback(() => {
    setWizardStep("context-view", "manual");
  }, [setWizardStep]);

  const goToQuestions = useCallback(() => {
    setWizardStep("questions", "manual");
  }, [setWizardStep]);

  // ── Re-generate ───────────────────────────────────────────────────────────
  const handleRegenerate = useCallback(() => {
    resumeSourcesFlowRef.current = true;
    contextTaskIdRef.current = null;
    questionsTaskIdRef.current = null;
    clearInFlightStep();
    setAnswers({});
    setCrawledPages([]);
    setSelectedUrls([]);
    setWizardStep("setup", "manual");
  }, [clearInFlightStep, setWizardStep]);

  // ── Strategic suggestions ─────────────────────────────────────────────────
  const handleGenerateSuggestions = useCallback(async () => {
    if (!api || isGeneratingSuggestions) return;
    setIsGeneratingSuggestions(true);
    try {
      const res = await api.suggest();
      const newTaskId = String(res.data.id);
      suggestionTaskIdRef.current = newTaskId;
      try {
        const listRes = await asyncTasksListing();
        for (const t of listRes.data ?? []) {
          if (
            t.type === ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION &&
            taskMatchesBoard(t, boardId) &&
            String(t.id) !== newTaskId
          ) {
            processedTaskIdsRef.current.add(String(t.id));
          }
        }
      } catch {
        // Polling will still mark older tasks via markOlderTasksProcessed
      }
    } catch {
      setIsGeneratingSuggestions(false);
      suggestionTaskIdRef.current = null;
      showToast({
        msg: "Failed to start strategic suggestions.",
        type: "error",
        duration: 4000,
      });
    }
  }, [api, boardId, isGeneratingSuggestions]);

  // ── Draft questions from store ────────────────────────────────────────────
  const draftQuestions = useMemo<DraftQuestion[]>(() => {
    return boardContextRecord?.draftQuestions?.questions ?? [];
  }, [boardContextRecord, boardContextVersion]);

  const value: BoardContextContextValue = {
    step,
    boardContextRecord,
    groups,
    statements,
    isLoadingMapUrl,
    crawledPages,
    hasContextView,
    hasDraftQuestions,
    goToContextView,
    goToQuestions,
    handleSetupSubmit,
    selectedUrls,
    setSelectedUrls,
    handleAnalysePages,
    isSubmittingPagePick,
    canContinueToQuestions,
    handleContinueToQuestions,
    draftQuestions,
    answers,
    setAnswer,
    handleBuildContext,
    isSubmittingAnswers,
    handleBackFromQuestions,
    handleRegenerate,
    isGeneratingSuggestions,
    handleGenerateSuggestions,
    isInitialising,
    hasOpenAIKey,
  };

  return <BoardContextContext.Provider value={value}>{children}</BoardContextContext.Provider>;
};

export const useBoardContextContext = () => {
  const ctx = useContext(BoardContextContext);
  if (!ctx) throw new Error("useBoardContextContext must be used within BoardContextProvider");
  return ctx;
};
